import { ensureAccessSession, installSessionRecovery } from '../features/shared/service/sessionProtocol';
/**
 * Serviço HTTP seguro sem exposição de tokens
 * Substitui o httpService.ts original
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../config/api';
import { attachRateLimitInterceptor } from '../features/shared/service/rateLimit';

class SecureHttpService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Configura interceptors para segurança
   */
  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // Adicionar token de autenticação se disponível
        const token = await ensureAccessSession();
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log apenas informações não sensíveis
        // console.log(`[HTTP] ${config.method?.toUpperCase()} ${config.url}`);

        // NÃO logar headers de autorização ou cookies
        return config;
      },
      (error) => {
        console.error('[HTTP] Erro na requisição:', error.message);
        return Promise.reject(error);
      }
    );

    // Ver features/shared/service/rateLimit.ts: 429 é interpretado, não repetido.
    attachRateLimitInterceptor(this.axiosInstance);
    installSessionRecovery(this.axiosInstance);

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // console.log(`[HTTP] ${response.status} ${response.config.url}`);

        return response;
      },
      (error) => {
        console.error('[HTTP] Erro na resposta:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
          // NÃO logar dados da resposta que podem conter tokens
        });

        // Session lifecycle belongs exclusively to installSessionRecovery.


        return Promise.reject(error);
      }
    );
  }

  /**
   * Remove informações sensíveis dos headers para logging
   */
  private sanitizeHeaders(headers: any): any {
    if (!headers) return headers;

    const sanitized = { ...headers };

    // Remover headers sensíveis
    delete sanitized['Authorization'];
    delete sanitized['authorization'];
    delete sanitized['Cookie'];
    delete sanitized['cookie'];
    delete sanitized['Set-Cookie'];
    delete sanitized['set-cookie'];

    return sanitized;
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get(url, config);
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const finalConfig = { ...config };

    // Se withCredentials for explicitamente passado no config, usar esse valor
    // Caso contrário, herda o padrão true da instância
    return this.axiosInstance.post(url, data, finalConfig);
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put(url, data, config);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete(url, config);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch(url, data, config);
  }

  /**
   * Upload de arquivo
   */
  async uploadFile(url: string, file: File, onProgress?: (progress: number) => void): Promise<AxiosResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  /**
   * Obtém instância do axios (para casos especiais)
   */
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  /**
   * Atualiza configurações base
   */
  updateBaseConfig(config: Partial<AxiosRequestConfig>): void {
    Object.assign(this.axiosInstance.defaults, config);
  }

  /**
   * Define header global
   */
  setHeader(key: string, value: string): void {
    this.axiosInstance.defaults.headers.common[key] = value;
  }

  /**
   * Remove header global
   */
  removeHeader(key: string): void {
    delete this.axiosInstance.defaults.headers.common[key];
  }
}

// Instância singleton
export const secureHttpService = new SecureHttpService();

export default secureHttpService;
